import { useEffect, useMemo } from 'react'

import { Code } from '@connectrpc/connect'
import { useTranslation } from 'react-i18next'

import { Alert } from '@enechain/ecloud-designsystem'

import { CrudModalLayout } from '~/components/common/CrudModalLayout'
import { RegionLoading } from '~/components/common/RegionLoading'
import { convertBusinessMasterListItems } from '~/hooks/businessMaster/useListBusinessMasters'
import { useCrudModalForm } from '~/hooks/useCrudModalForm'
import { todayCivilDate } from '~/models/civilDate'
import { isActiveOn } from '~/models/validPeriod'
import {
  convertBgMasterListItems,
  convertListBgMastersResponse,
  useListBgMasters,
} from '~/pages/AdminOcctoMaster/tabs/BgMaster/hooks/useListBgMasters'
import { DemandSuppressionPlanMasterFormFields } from '~/pages/AdminOcctoMaster/tabs/DemandSuppressionPlanMaster/components/DemandSuppressionPlanMasterFormFields'
import { useGetDemandSuppressionMaster } from '~/pages/AdminOcctoMaster/tabs/DemandSuppressionPlanMaster/hooks/useGetDemandSuppressionMaster'
import { useGetDemandSuppressionPlanMasterFormOptions } from '~/pages/AdminOcctoMaster/tabs/DemandSuppressionPlanMaster/hooks/useGetDemandSuppressionPlanMasterFormOptions'
import { useUpdateDemandSuppressionMaster } from '~/pages/AdminOcctoMaster/tabs/DemandSuppressionPlanMaster/hooks/useUpdateDemandSuppressionMaster'
import {
  type DemandSuppressionMasterFormValues,
  demandSuppressionMasterFormSchema,
} from '~/pages/AdminOcctoMaster/tabs/DemandSuppressionPlanMaster/models/schema'
import { resolveDemandSuppressionMasterDetailedError } from '~/pages/AdminOcctoMaster/tabs/DemandSuppressionPlanMaster/models/serverError'
import type { DemandSuppressionMasterRow } from '~/pages/AdminOcctoMaster/tabs/DemandSuppressionPlanMaster/types'
import { convertSubmitterMasterListItems } from '~/pages/AdminOcctoMaster/tabs/SubmitterMaster/hooks/useListSubmitterMasters'
import { mapMasterGetError } from '~/pages/AdminOcctoMaster/tabs/_common/getError'
import { findBgsOutOfPeriod } from '~/pages/AdminOcctoMaster/tabs/_common/models/planMasterBgValidation'

type Props = {
  isOpen: boolean
  row: DemandSuppressionMasterRow
  onClose: () => void
}

/**
 * 需要抑制計画マスタ 編集モーダル。
 *
 * useGetDemandSuppressionMaster で full entity (contracts / partnerBgs 含む) を
 * 取得して form を populate する。
 * submitterCode / submitterMasterId は登録後変更不可のため readonly で表示する。
 * note のみ編集可。contracts / partnerBgs は更新時も既存値をそのまま渡す。
 */
export const EditDemandSuppressionPlanMasterModal = ({
  isOpen,
  row,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { mutateAsync, isPending } = useUpdateDemandSuppressionMaster()
  const {
    data,
    isPending: isFetching,
    isError,
    error,
  } = useGetDemandSuppressionMaster(row.id)
  // 集約 RPC: 区分フィルタ済みの suppressionBgs / partnerBgs / businessMasters を取得
  const { data: formOptions } = useGetDemandSuppressionPlanMasterFormOptions()
  // 削除済み判定専用: 絞り込み前の全 BG マスタ (4526-plan-master-valid-period-filter
  // 設計書 §4、AC-06)。
  const { data: allBgMastersData } = useListBgMasters()
  // list クエリ取得中は undefined (fallback 解決の「(削除済み)」誤表示防止、
  // PR #5920 レビュー指摘)。
  const allBgMasters = useMemo(
    () =>
      allBgMastersData !== undefined
        ? convertListBgMastersResponse(allBgMastersData)
        : undefined,
    [allBgMastersData],
  )

  // 集約 RPC の submitters (DR 区分フィルタ済み) → submitterAreaCode 解決用
  const submitters = useMemo(
    () =>
      formOptions !== undefined
        ? convertSubmitterMasterListItems(formOptions.submitters)
        : [],
    [formOptions],
  )
  // 集約 RPC の suppressionBgs (DR 区分フィルタ済み) → SuppressionContractsSection 用
  const suppressionBgs = useMemo(
    () =>
      formOptions !== undefined
        ? convertBgMasterListItems(formOptions.suppressionBgs)
        : [],
    [formOptions],
  )
  // 集約 RPC の partnerBgs (全区分) → SuppressionContractsSection + SuppressionPartnerBgsSection 用
  const partnerBgs = useMemo(
    () =>
      formOptions !== undefined
        ? convertBgMasterListItems(formOptions.partnerBgs)
        : [],
    [formOptions],
  )
  // 集約 RPC の businesses → SuppressionContractsSection 用 (proto field: businesses)
  const businessMasters = useMemo(
    () =>
      formOptions !== undefined
        ? convertBusinessMasterListItems(formOptions.businesses)
        : [],
    [formOptions],
  )

  const fetched = data?.demandSuppressionMaster

  const {
    control,
    errors,
    serverError,
    handleClose,
    handleSave: hookHandleSave,
    setError,
    getValues,
    reset,
    isDirty,
  } = useCrudModalForm<DemandSuppressionMasterFormValues>({
    schema: demandSuppressionMasterFormSchema,
    defaultValues: {
      submitterMasterId: '',
      submitterCode: '',
      note: '',
      contracts: [],
      partnerBgs: [],
      validFrom: todayCivilDate(),
      validTo: undefined,
    },
    onSubmit: async (values) => {
      if (fetched?.updatedAt === undefined) {
        throw new Error(
          t(
            'page:adminOcctoMaster.demandSuppressionPlanMaster.serverError.unknown',
          ),
        )
      }
      await mutateAsync({
        id: row.id,
        note:
          values.note !== undefined && values.note !== ''
            ? values.note
            : undefined,
        contracts: values.contracts.map((c) => ({
          suppressionBgMasterId: c.suppressionBgMasterId,
          partnerBgMasterId: c.partnerBgMasterId,
          businessMasterId: c.businessMasterId,
          contractId: c.contractId,
          validFrom: c.validFrom,
          validTo: c.validTo,
        })),
        partnerBgs: values.partnerBgs.map((p) => ({
          side: p.side,
          bgMasterId: p.bgMasterId,
          validFrom: p.validFrom,
          validTo: p.validTo,
        })),
        validFrom: values.validFrom,
        validTo: values.validTo,
        expectedUpdatedAt: fetched.updatedAt,
      })
    },
    onClose,
    customErrorKeys: {
      [Code.Aborted]:
        'page:adminOcctoMaster.demandSuppressionPlanMaster.serverError.aborted',
      [Code.NotFound]:
        'page:adminOcctoMaster.demandSuppressionPlanMaster.serverError.notFound',
      [Code.InvalidArgument]:
        'page:adminOcctoMaster.demandSuppressionPlanMaster.serverError.invalidArgument',
    },
    unknownErrorKey:
      'page:adminOcctoMaster.demandSuppressionPlanMaster.serverError.unknown',
    // SUPPRESSION_BG_NOT_FOUND / SUPPRESSION_BG_LICENSE_MISMATCH /
    // PARTNER_BG_NOT_FOUND / BUSINESS_NOT_FOUND は Update でも到達しうる
    // (contracts[] / partnerBgs[] は編集可能なので Create/Edit 両方に
    // 配線する)。 submitter 側の reason / duplicate
    // は submitter が immutable のため Update では到達しない。
    resolveDetailedError: resolveDemandSuppressionMasterDetailedError,
  })

  // submitterCode と一致する submitter の areaCode を解決
  const submitterAreaCode = useMemo(() => {
    const code = fetched?.submitterCode
    if (code === undefined || code === '') return undefined
    return submitters.find((s) => s.submitterCode === code)?.areaCode
  }, [fetched?.submitterCode, submitters])

  useEffect(() => {
    if (fetched !== undefined) {
      reset({
        submitterMasterId: fetched.submitterMasterId,
        submitterCode: fetched.submitterCode,
        note: fetched.note ?? '',
        contracts: fetched.contracts.map((c) => ({
          suppressionBgMasterId: c.suppressionBgMasterId,
          partnerBgMasterId: c.partnerBgMasterId,
          businessMasterId: c.businessMasterId,
          contractId: c.contractId,
          validFrom:
            c.validFrom !== undefined
              ? {
                  year: c.validFrom.year,
                  month: c.validFrom.month,
                  day: c.validFrom.day,
                }
              : todayCivilDate(),
          validTo:
            c.validTo !== undefined
              ? {
                  year: c.validTo.year,
                  month: c.validTo.month,
                  day: c.validTo.day,
                }
              : undefined,
        })),
        partnerBgs: fetched.partnerBgs.map((p) => ({
          side: p.side,
          bgMasterId: p.bgMasterId,
          validFrom:
            p.validFrom !== undefined
              ? {
                  year: p.validFrom.year,
                  month: p.validFrom.month,
                  day: p.validFrom.day,
                }
              : todayCivilDate(),
          validTo:
            p.validTo !== undefined
              ? {
                  year: p.validTo.year,
                  month: p.validTo.month,
                  day: p.validTo.day,
                }
              : undefined,
        })),
        // AC-05: 保存済みの ValidFrom (適用開始日、編集可能) / ValidTo を表示する。
        validFrom:
          fetched.validFrom !== undefined
            ? {
                year: fetched.validFrom.year,
                month: fetched.validFrom.month,
                day: fetched.validFrom.day,
              }
            : todayCivilDate(),
        validTo:
          fetched.validTo !== undefined
            ? {
                year: fetched.validTo.year,
                month: fetched.validTo.month,
                day: fetched.validTo.day,
              }
            : undefined,
      })
    }
  }, [fetched, reset])

  // contracts / partnerBgs の選択済み BG が validFrom narrow 後の候補外なら
  // hook の handleSave 前に field-level エラーで block する (silent invalid
  // submission 防止)。 Edit modal では submitter は immutable のため submitter
  // period check は不要 (Create 側のみ)。
  const validateBgsOutOfPeriodBeforeSubmit = (): boolean => {
    const values = getValues()
    const recordValidFrom = values.validFrom
    const isActiveForRecord = (bg: (typeof suppressionBgs)[number]): boolean =>
      (submitterAreaCode === undefined || bg.areaCode === submitterAreaCode) &&
      (recordValidFrom === undefined ||
        isActiveOn(bg.validFrom, bg.validTo, recordValidFrom))
    const suppressionBgCandidates = suppressionBgs.filter(isActiveForRecord)
    const partnerBgCandidates = partnerBgs.filter(isActiveForRecord)
    const bgOutOfPeriodMsg = t(
      'page:adminOcctoMaster.demandSuppressionPlanMaster.form.bgMasterOutOfPeriod',
    )
    const allBgMastersForValidation =
      allBgMastersData !== undefined ? allBgMasters : undefined
    const contractsSupp = findBgsOutOfPeriod(
      values.contracts.map((c) => ({ bgMasterId: c.suppressionBgMasterId })),
      suppressionBgCandidates,
      allBgMastersForValidation,
    )
    const contractsPartner = findBgsOutOfPeriod(
      values.contracts.map((c) => ({ bgMasterId: c.partnerBgMasterId })),
      partnerBgCandidates,
      allBgMastersForValidation,
    )
    const partnerBgsOut = findBgsOutOfPeriod(
      values.partnerBgs,
      partnerBgCandidates,
      allBgMastersForValidation,
    )
    if (
      contractsSupp.length === 0 &&
      contractsPartner.length === 0 &&
      partnerBgsOut.length === 0
    ) {
      return true
    }
    for (const { index } of contractsSupp) {
      setError(`contracts.${index}.suppressionBgMasterId`, {
        message: bgOutOfPeriodMsg,
      })
    }
    for (const { index } of contractsPartner) {
      setError(`contracts.${index}.partnerBgMasterId`, {
        message: bgOutOfPeriodMsg,
      })
    }
    for (const { index } of partnerBgsOut) {
      setError(`partnerBgs.${index}.bgMasterId`, {
        message: bgOutOfPeriodMsg,
      })
    }
    return false
  }

  const handleSave = (): void => {
    if (!validateBgsOutOfPeriodBeforeSubmit()) return
    hookHandleSave()
  }

  return (
    <CrudModalLayout
      handleSave={handleSave}
      isDirty={isDirty}
      isOpen={isOpen}
      isPending={isPending}
      isSaveDisabled={isFetching || isError}
      onClose={handleClose}
      serverError={serverError}
      // design (`design/admin/occto.html` #pm-supp-modal) は `.ecl-modal--xl`。
      size="xl"
      title={t(
        'page:adminOcctoMaster.demandSuppressionPlanMaster.modal.editTitle',
      )}
    >
      {isError && (
        <Alert
          status="error"
          title={mapMasterGetError(
            t,
            error,
            'page:adminOcctoMaster.demandSuppressionPlanMaster.serverError',
          )}
        />
      )}
      <RegionLoading isLoading={isFetching}>
        {isFetching ? null : (
          <DemandSuppressionPlanMasterFormFields
            allBgMasters={allBgMasters}
            businessMasters={businessMasters}
            control={control}
            errors={errors}
            isEdit
            partnerBgs={partnerBgs}
            submitterAreaCode={submitterAreaCode}
            suppressionBgs={suppressionBgs}
          />
        )}
      </RegionLoading>
    </CrudModalLayout>
  )
}
